import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layers, Download, ChevronRight, X, DollarSign, Plus, Edit2 } from 'lucide-react';
import { api } from '../api/client';
import { useTenant } from '../context/TenantContext';
import { WorkItemEditor, AddWorkItemButton, InlineEditCell } from '../components/WorkItemEditor';
import { UpgradeBanner } from '../components/UpgradeBanner';
import { BulkEditToolbar, SelectableCheckbox, SelectAllCheckbox, useSelectable } from '../components/BulkEditToolbar';
import { FilterBar2 } from '../components/filters';
import { PageErrorBoundary, PaneErrorBoundary } from '../components/ErrorBoundary';
import { SkeletonTable, SkeletonKPIGrid, SkeletonPaneContent } from '../components/Skeleton';
import { NoEpicsState, NoResultsState, NoCostDataState } from '../components/EmptyState';
import { normalizeEpic, toNumber, formatCurrency as safeCurrency, clamp } from '../utils/dataAdapters';
import Breadcrumbs, { getWorkItemBreadcrumbs } from '../components/Breadcrumbs';
import SavedViewsDropdown from '../components/SavedViewsDropdown';

const STATUS_COLORS = {
  'Blocked': '#8E44AD',
  'Behind': '#E74C3C',
  'Due Date at Risk': '#F1C40F',
  'In Progress': '#3498DB',
  'In Review': '#16A085',
  'Not Started': '#BDC3C7',
  'Completed': '#2ECC71',
  'Not Required': '#95A5A6',
  'Unknown': '#9CA3AF',
};

const EpicsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canEdit, isLowerTier } = useTenant();
  const [epics, setEpics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEpic, setSelectedEpic] = useState(null);
  const [epicDetails, setEpicDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  
  // New Filter Bar 2.0 state
  const [advancedFilters, setAdvancedFilters] = useState(() => {
    // Initialize from URL params
    const status = searchParams.get('status');
    const priority = searchParams.get('priority');
    const project_id = searchParams.get('project_id');
    return {
      status: status ? status.split(',') : [],
      priority: priority ? priority.split(',') : [],
      project_id: project_id ? [project_id] : [],
    };
  });
  const [quickFilters, setQuickFilters] = useState({
    blocked_only: false,
    behind_only: false,
    at_risk_only: false,
    due_this_week: false,
    my_work_only: false,
  });
  const [filterOptions, setFilterOptions] = useState({ portfolios: [], programs: [], projects: [], statuses: [] });
  
  // Editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editingEpic, setEditingEpic] = useState(null);
  const [selectedProjectForCreate, setSelectedProjectForCreate] = useState(null);

  // Bulk edit selection
  const {
    selectedIds,
    toggleSelection,
    selectAll,
    clearSelection,
    isSelected,
    allSelected,
    someSelected
  } = useSelectable(epics);

  useEffect(() => {
    loadFilters();
  }, []);

  // Reload epics when filters change
  useEffect(() => {
    loadEpics();
    // Update URL with current filters
    const params = new URLSearchParams();
    if (advancedFilters.status?.length > 0) params.set('status', advancedFilters.status.join(','));
    if (advancedFilters.priority?.length > 0) params.set('priority', advancedFilters.priority.join(','));
    if (advancedFilters.project_id?.length > 0) params.set('project_id', advancedFilters.project_id[0]);
    setSearchParams(params, { replace: true });
  }, [advancedFilters, quickFilters]);

  const loadFilters = async () => {
    try {
      const res = await api.getFilters();
      setFilterOptions({
        portfolios: res.data.portfolios || [],
        programs: res.data.programs || [],
        projects: res.data.projects || [],
        statuses: res.data.statuses || []
      });
    } catch (error) {
      console.error('Failed to load filters:', error);
    }
  };

  const loadEpics = async () => {
    try {
      setLoading(true);
      // Build params from advanced filters
      const params = {};
      
      // Multi-select filters - join arrays with comma
      if (advancedFilters.status?.length > 0) params.status = advancedFilters.status.join(',');
      if (advancedFilters.priority?.length > 0) params.priority = advancedFilters.priority.join(',');
      if (advancedFilters.project_id?.length > 0) params.project_id = advancedFilters.project_id[0];
      if (advancedFilters.portfolio_id?.length > 0) params.portfolio_id = advancedFilters.portfolio_id[0];
      if (advancedFilters.program_id?.length > 0) params.program_id = advancedFilters.program_id[0];
      
      // Quick filters override status
      if (quickFilters.blocked_only) params.status = 'Blocked';
      if (quickFilters.behind_only) params.status = 'Behind';
      if (quickFilters.at_risk_only) params.status = 'Behind,Due Date at Risk,Blocked';
      
      const res = await api.getEpics(params);
      setEpics(res.data.epics || []);
    } catch (error) {
      console.error('Failed to load epics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEpicDetails = async (epicId) => {
    try {
      setDetailsLoading(true);
      setDetailsError(null);
      const res = await api.getEpicDetails(epicId);
      setEpicDetails(res.data);
    } catch (err) {
      console.error('Failed to load epic details:', err);
      setDetailsError(err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleEpicClick = (epic) => {
    if (!epic?.id) return;
    setSelectedEpic(epic);
    setEpicDetails(null);
    loadEpicDetails(epic.id);
  };

  const formatCurrency = (value) => safeCurrency(value, '$');

  // Calculate KPIs (null-safe)
  const kpis = {
    total: epics.length,
    avgComplete: epics.length > 0 ? Math.round(epics.reduce((sum, e) => sum + toNumber(e?.percent_complete), 0) / epics.length) : 0,
    blocked: epics.filter(e => e && toNumber(e.blocked_count) > 0).length,
    behind: epics.filter(e => e?.status === 'Behind').length,
    totalVariance: epics.reduce((sum, e) => sum + toNumber(e?.variance), 0)
  };

  // Editor handlers
  const handleAddEpic = () => {
    setEditingEpic(null);
    // Use the first project from filters or first available
    const projectId = advancedFilters.project_id?.[0] || (filterOptions.projects[0]?.id);
    setSelectedProjectForCreate(projectId);
    setShowEditor(true);
  };

  const handleEditEpic = (epic) => {
    setEditingEpic(epic);
    setSelectedProjectForCreate(epic.project_id);
    setShowEditor(true);
  };

  const handleEditorSave = () => {
    loadEpics(); // Refresh list
  };

  const handleEditorDelete = (id) => {
    setEpics(epics.filter(e => e.id !== id));
  };

  const handleInlineUpdate = (itemId, field, newValue) => {
    setEpics(epics.map(e => 
      e.id === itemId ? { ...e, [field]: newValue } : e
    ));
  };

  const handleBulkUpdate = (field, value) => {
    // Update local state for selected items
    setEpics(epics.map(e => 
      selectedIds.includes(e.id) ? { ...e, [field]: value } : e
    ));
    clearSelection();
  };

  return (
    <div>
      {/* Upgrade Banner for lower tiers */}
      {isLowerTier() && (
        <UpgradeBanner feature="in-app epic editing" className="mb-6" />
      )}

      {/* Sprint 18: Breadcrumbs */}
      <Breadcrumbs items={getWorkItemBreadcrumbs('epics')} className="mb-4" />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
            <Layers className="w-7 h-7 text-[var(--clr-brand-primary)]" />
            Epics
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">Manage and track all epics across portfolios</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Sprint 19: Saved Views */}
          <SavedViewsDropdown 
            currentViewType="epics" 
            onNavigate={(path) => navigate(path)} 
          />
          <AddWorkItemButton 
            itemType="epic" 
            onClick={handleAddEpic}
          />
          <a
            href={api.exportCSV('epics', { 
              ...(advancedFilters.status?.length > 0 ? { status: advancedFilters.status.join(',') } : {}),
              ...(advancedFilters.project_id?.length > 0 ? { project_id: advancedFilters.project_id[0] } : {}),
            })}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-surface-subtle)] hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </a>
        </div>
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-[var(--clr-surface-card)] rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-[var(--clr-text-secondary)] mb-1">Total Epics</p>
          <p className="text-2xl font-bold text-[var(--clr-text-primary)]">{kpis.total}</p>
        </div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-[var(--clr-text-secondary)] mb-1">Avg % Complete</p>
          <p className="text-2xl font-bold text-[var(--clr-brand-primary)]">{kpis.avgComplete}%</p>
        </div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-[var(--clr-text-secondary)] mb-1">With Blockers</p>
          <p className="text-2xl font-bold text-purple-600">{kpis.blocked}</p>
        </div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-[var(--clr-text-secondary)] mb-1">Behind Schedule</p>
          <p className="text-2xl font-bold text-[var(--clr-sev-blocked)]">{kpis.behind}</p>
        </div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-[var(--clr-text-secondary)] mb-1">Cost Variance</p>
          <p className={`text-2xl font-bold ${kpis.totalVariance > 0 ? 'text-[var(--clr-sev-blocked)]' : 'text-[var(--clr-sev-healthy)]'}`}>
            {kpis.totalVariance > 0 ? '+' : ''}{formatCurrency(kpis.totalVariance)}
          </p>
        </div>
      </div>

      {/* Filter Bar 2.0 */}
      <FilterBar2
        pageType="epics"
        filters={advancedFilters}
        onFiltersChange={setAdvancedFilters}
        quickFilters={quickFilters}
        onQuickFiltersChange={setQuickFilters}
        counts={{
          blocked: kpis.blocked,
          behind: kpis.behind,
          atRisk: epics.filter(e => e && ['Behind', 'Due Date at Risk', 'Blocked'].includes(e.status)).length,
        }}
        showInsightsBar={true}
        className="mb-6"
      />

      {/* Epics Table */}
      <div className="bg-[var(--clr-surface-card)] rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--clr-brand-primary)]"></div>
          </div>
        ) : epics.length === 0 ? (
          <div className="text-center py-12">
            <Layers className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-[var(--clr-text-secondary)]">No epics found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[var(--clr-surface-subtle)] border-b border-gray-200">
                <tr>
                  <th className="w-10 px-2">
                    <SelectAllCheckbox
                      allSelected={allSelected}
                      someSelected={someSelected}
                      onSelectAll={selectAll}
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Epic</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Project</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">% Complete</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Features</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Est Cost</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Act Cost</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Variance</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Dates</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {epics.filter(Boolean).map((epic) => {
                  const isMatchedItem = epic._is_matched === true;
                  const isParentContext = epic._is_parent === true && !epic._is_matched;
                  
                  return (
                  <tr
                    key={epic.id}
                    onClick={() => handleEpicClick(epic)}
                    className={`hover:bg-[var(--clr-surface-hover)] cursor-pointer transition-colors ${
                      isSelected(epic.id) ? 'bg-[var(--clr-brand-primary)] bg-opacity-10' : ''
                    } ${isMatchedItem ? 'bg-yellow-50 border-l-4 border-l-yellow-400' : ''}`}
                    data-testid={`epic-row-${epic.id}`}
                  >
                    <td className="px-2 py-3">
                      <SelectableCheckbox
                        checked={isSelected(epic.id)}
                        onChange={() => toggleSelection(epic.id)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isMatchedItem && (
                          <span className="px-1.5 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded" title="Matches filter">
                            Match
                          </span>
                        )}
                        <InlineEditCell
                          value={epic.name}
                          field="name"
                          itemId={epic.id}
                          itemType="epic"
                          onUpdate={handleInlineUpdate}
                        />
                      </div>
                      <div className="text-xs text-[var(--clr-text-muted)]">{epic.portfolio_name} / {epic.program_name}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{epic.project_name}</td>
                    <td className="px-4 py-3">
                      <InlineEditCell
                        value={epic.status}
                        field="status"
                        type="select"
                        options={Object.keys(STATUS_COLORS)}
                        itemId={epic.id}
                        itemType="epic"
                        onUpdate={handleInlineUpdate}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-[var(--clr-brand-primary)] h-2 rounded-full"
                            style={{ width: `${epic.percent_complete}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium">{epic.percent_complete}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{epic.feature_count}</td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{formatCurrency(epic.est_cost)}</td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{formatCurrency(epic.act_cost)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-medium ${epic.variance > 0 ? 'text-[var(--clr-sev-blocked)]' : 'text-[var(--clr-sev-healthy)]'}`}>
                        {epic.variance > 0 ? '+' : ''}{formatCurrency(epic.variance)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--clr-text-muted)]">
                      {epic.start_date && <div>Start: {epic.start_date}</div>}
                      {epic.due_date && <div>Due: {epic.due_date}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {canEdit() && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditEpic(epic); }}
                            className="p-1.5 hover:bg-[var(--clr-brand-primary)] bg-opacity-10 rounded text-[var(--clr-brand-primary)]"
                            title="Edit Epic"
                            data-testid={`edit-epic-${epic.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        )}
                        <ChevronRight className="w-4 h-4 text-[var(--clr-text-muted)]" />
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Epic Details Drawer */}
      {selectedEpic && (
        <div className="fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSelectedEpic(null)}></div>
          <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-white shadow-xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-[var(--clr-text-primary)]">{selectedEpic.name}</h2>
                <p className="text-sm text-[var(--clr-text-muted)]">{selectedEpic.project_name}</p>
              </div>
              <div className="flex items-center gap-2">
                {canEdit() && (
                  <button 
                    onClick={() => { setSelectedEpic(null); handleEditEpic(selectedEpic); }}
                    className="px-3 py-1.5 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] flex items-center gap-2 text-sm"
                    data-testid="drawer-edit-epic-btn"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                )}
                <button onClick={() => setSelectedEpic(null)} className="p-2 hover:bg-[var(--clr-surface-hover)] rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {epicDetails ? (
              <div className="p-6 space-y-6">
                {/* Status & Progress */}
                <div className="flex items-center gap-4">
                  <span
                    className="px-3 py-1 rounded-full text-sm font-medium text-white"
                    style={{ backgroundColor: STATUS_COLORS[epicDetails.epic?.status] || '#95A5A6' }}
                  >
                    {epicDetails.epic?.status || 'Not Set'}
                  </span>
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span>Progress</span>
                      <span className="font-medium">{epicDetails.epic?.percent_complete || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-[var(--clr-brand-primary)] h-2 rounded-full" style={{ width: `${epicDetails.epic?.percent_complete || 0}%` }}></div>
                    </div>
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                    <p className="text-xs text-[var(--clr-text-muted)] mb-1">Start Date</p>
                    <p className="text-sm font-medium">{epicDetails.epic?.start_date || '-'}</p>
                  </div>
                  <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                    <p className="text-xs text-[var(--clr-text-muted)] mb-1">Due Date</p>
                    <p className="text-sm font-medium">{epicDetails.epic?.due_date || '-'}</p>
                  </div>
                  <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                    <p className="text-xs text-[var(--clr-text-muted)] mb-1">Revised Date</p>
                    <p className="text-sm font-medium">{epicDetails.epic?.revised_date || '-'}</p>
                  </div>
                </div>

                {/* Cost Metrics */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-[var(--clr-text-secondary)]" />
                    Cost Summary
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    {/* Estimated - Blue theme with proper contrast */}
                    <div className="metric-card metric-brand rounded-lg p-3" data-testid="epic-cost-estimated">
                      <p className="metric-card-label text-xs mb-1">Estimated</p>
                      <p className="metric-card-value text-lg font-bold">
                        {formatCurrency(epicDetails?.epic?.cost_metrics?.est_cost)}
                      </p>
                    </div>
                    {/* Actual - Green theme */}
                    <div className="metric-card metric-success rounded-lg p-3" data-testid="epic-cost-actual">
                      <p className="metric-card-label text-xs mb-1">Actual</p>
                      <p className="metric-card-value text-lg font-bold">
                        {formatCurrency(epicDetails?.epic?.cost_metrics?.act_cost)}
                      </p>
                    </div>
                    {/* Variance - Red/Green based on value */}
                    <div 
                      className={`metric-card rounded-lg p-3 ${toNumber(epicDetails?.epic?.cost_metrics?.variance) > 0 ? 'metric-danger' : 'metric-success'}`}
                      data-testid="epic-cost-variance"
                    >
                      <p className="metric-card-label text-xs mb-1">Variance</p>
                      <p className="metric-card-value text-lg font-bold">
                        {toNumber(epicDetails?.epic?.cost_metrics?.variance) > 0 ? '+' : ''}
                        {formatCurrency(epicDetails?.epic?.cost_metrics?.variance)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Features List */}
                {epicDetails.features?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Features ({epicDetails.features.length})</h3>
                    <div className="space-y-2">
                      {epicDetails.features.map((feature) => (
                        <div key={feature.id} className="flex items-center justify-between p-3 bg-[var(--clr-surface-subtle)] rounded-lg">
                          <div>
                            <p className="font-medium text-[var(--clr-text-primary)]">{feature.name}</p>
                            <p className="text-xs text-[var(--clr-text-muted)]">{feature.item_count} items</p>
                          </div>
                          <div className="text-right">
                            <span
                              className="inline-block px-2 py-0.5 rounded text-xs font-medium text-white"
                              style={{ backgroundColor: STATUS_COLORS[feature.status] || '#95A5A6' }}
                            >
                              {feature.status}
                            </span>
                            <p className="text-sm font-medium mt-1">{feature.percent_complete}%</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Links */}
                <div className="pt-4 border-t border-gray-200 space-y-2">
                  <button
                    onClick={() => navigate(`/project/${epicDetails.epic.project_id}`)}
                    className="w-full text-left px-4 py-2 bg-[var(--clr-surface-subtle)] hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
                  >
                    Open Project Dashboard →
                  </button>
                  <button
                    onClick={() => navigate(`/features?epic_id=${selectedEpic.id}`)}
                    className="w-full text-left px-4 py-2 bg-[var(--clr-surface-subtle)] hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
                  >
                    View All Features →
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--clr-brand-primary)]"></div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Work Item Editor Modal */}
      <WorkItemEditor
        isOpen={showEditor}
        onClose={() => { setShowEditor(false); setEditingEpic(null); }}
        itemType="epic"
        item={editingEpic}
        parentId={selectedProjectForCreate}
        projectId={selectedProjectForCreate}
        onSave={handleEditorSave}
        onDelete={handleEditorDelete}
      />

      {/* Bulk Edit Toolbar */}
      <BulkEditToolbar
        selectedIds={selectedIds}
        itemType="epic"
        onBulkUpdate={handleBulkUpdate}
        onClearSelection={clearSelection}
      />
    </div>
  );
};

// Wrap with PageErrorBoundary for top-level error handling
const EpicsPageWithErrorBoundary = () => (
  <PageErrorBoundary pageName="Epics">
    <EpicsPage />
  </PageErrorBoundary>
);

export default EpicsPageWithErrorBoundary;
